import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button"
import { Label } from "@/shared/ui/label"
import { Textarea } from "@/shared/ui/textarea"
import Loader from '@/shared/components/Loader';
import { toast } from "react-toastify";
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { useUpdateReportDetailsMutation } from "@/api/rtk/mutationsApi"
import { reportsApi } from "@/api/rtk/reportsApi"
import { setSelectedReport } from "@/store/slices/ReportsSlice"

function ReportEditForm({
  report,
  queryArgs,
  isDrawer = false,
  setEditClicked,
  staticFields,
  setStaticFields,
  dynamicFields,
  setDynamicFields,
  handleInputChange,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentToolFields, generation_name } = useCurrentTool();
  const [editLoading, setEditLoading] = useState(false);
  const [updateReportDetails] = useUpdateReportDetailsMutation();

  useEffect(() => {
    const updatedDynamicFields = currentToolFields.reduce((acc, field) => {
      acc[field.name] = report?.fields?.[field.name] || '';
      return acc;
    }, {});

    setDynamicFields(updatedDynamicFields);
    setStaticFields({ comment: report?.comment || '', report: report?.report || ''});
  }, [report, currentToolFields, setDynamicFields, setStaticFields]);

  const handleEditSave = async () => {
    setEditLoading(true);

    const tool_fields = currentToolFields.map((field) => ({
      id: field.id || '',
      value: dynamicFields[field.name] || '',
    }));

    const dataToSend = {
      ...staticFields,
      id: report.id,
      tool_fields
    };

    try {
      const detailUpdated = await updateReportDetails(dataToSend).unwrap();
      if (detailUpdated?.id) {
        const fields = {
          ...(report.fields || {}),
          ...Object.fromEntries(
            currentToolFields.map((field) => [field.name, dynamicFields[field.name] || ''])
          ),
        };
        const patch = {
          comment: staticFields.comment,
          report: staticFields.report,
          fields,
        };

        if (isDrawer) {
          dispatch(setSelectedReport({ ...report, ...patch }));
        } else if (queryArgs) {
          dispatch(reportsApi.util.updateQueryData('getReport', queryArgs, (draft) => {
            const cached = draft?.message?.[0];
            if (!cached) return;
            Object.assign(cached, patch);
          }));
        }
        toast.success(`${generation_name} updated successfully!`)
        setEditClicked(false);
      }
    } catch {
      toast.error('Failed to update report details. Please try again.');
    }
    setEditLoading(false);
  };

  const handleEditCancel = () => {
    setEditClicked(false);
    setEditLoading(false);
    setDynamicFields(currentToolFields.reduce((acc, field) => {
      acc[field.name] = report.fields?.[field.name] || '';
      return acc;
    }, {}));
    setStaticFields({ comment: report?.comment || '', report: report?.report || ''});
  }

  return (
    <>
      <div className="flex flex-col gap-1.5 -mt-6">
        <Label>{t("common.reportText")}</Label>
        <Textarea
          className='h-[45vh] no-scrollbar'
          value={staticFields.report || ''}
          onChange={(e) => handleInputChange({ key: 'report', value: e.target.value })}
        />
      </div>
      
      <div className="flex flex-col gap-1.5">
        <Label>{t("common.comment")}</Label>
        <Textarea
          className='h-28'
          value={staticFields.comment || ''}
          onChange={(e) => handleInputChange({ key: 'comment', value: e.target.value })}
          type='text'
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3 pt-1 mb-5">
        <Button onClick={handleEditCancel} variant='outline'>
          {t("actions.cancel")}
        </Button>
        <Button onClick={!editLoading && handleEditSave} disabled={editLoading}>
          {editLoading ? <Loader variant={'small'}/> : t("actions.save")}
        </Button>
      </div>
    </>
  );
}

export default ReportEditForm;
